import { supabase } from '@/integrations/supabase/client';
import { RevisionSession, ChangeItem, ChangeItemOption } from '@/types/resumeRevision';

export class ResumeRevisionService {
  
  /**
   * Create a new revision session
   */
  static async createRevisionSession(data: {
    resume_id: string;
    session_name: string;
    revision_notes?: string;
    additional_documents?: Array<{
      filename: string;
      content: string;
      type: string;
    }>;
  }): Promise<RevisionSession> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get the resume content
    const { data: resume, error: resumeError } = await supabase
      .from('resumes')
      .select('*')
      .eq('id', data.resume_id)
      .maybeSingle();

    if (resumeError) {
      throw new Error(`Failed to fetch resume: ${resumeError.message}`);
    }

    if (!resume) {
      throw new Error('Resume not found');
    }

    // Create the revision session
    const sessionData = {
      user_id: user.id,
      resume_id: data.resume_id,
      session_name: data.session_name,
      original_resume_content: resume.parsed_resume_data || resume.content || {},
      current_resume_content: resume.parsed_resume_data || resume.content || {},
      additional_documents: data.additional_documents || [],
      revision_notes: data.revision_notes,
      status: 'active' as const,
      accuracy_report: {},
      change_log: []
    };

    const { data: session, error: sessionError } = await supabase
      .from('revision_sessions')
      .insert(sessionData)
      .select()
      .single();

    if (sessionError) {
      throw new Error(`Failed to create revision session: ${sessionError.message}`);
    }

    return session as unknown as RevisionSession;
  }

  /**
   * Process revision instructions and generate change items
   */
  static async processRevisionInstructions(sessionId: string): Promise<{
    success: boolean;
    change_items_created: number;
    options_generated: number;
    questions: string[];
  }> {
    const { data: session, error: sessionError } = await supabase
      .from('revision_sessions')
      .select('*')
      .eq('id', sessionId)
      .maybeSingle();

    if (sessionError || !session) {
      throw new Error('Revision session not found');
    }

    const { data: { session: authSession } } = await supabase.auth.getSession();
    
    if (!authSession?.access_token) {
      throw new Error('Authentication required');
    }

    const response = await supabase.functions.invoke('process-revision-session', {
      body: {
        session_id: sessionId,
        revision_notes: session.revision_notes || '',
        resume_content: session.current_resume_content,
        additional_documents: session.additional_documents
      },
      headers: {
        'Authorization': `Bearer ${authSession.access_token}`
      }
    });

    if (response.error) {
      throw new Error(`Failed to process revision instructions: ${response.error.message}`);
    }

    return response.data;
  }

  /**
   * Get change items for a revision session
   */
  static async getChangeItems(sessionId: string): Promise<ChangeItem[]> {
    // 1) Fetch items without embedding to avoid ambiguous FK relationships
    const { data: items, error: itemsError } = await supabase
      .from('change_items')
      .select('*')
      .eq('revision_session_id', sessionId)
      .order('created_at');

    if (itemsError) {
      throw new Error(`Failed to fetch change items: ${itemsError.message}`);
    }

    if (!items || items.length === 0) return [] as unknown as ChangeItem[];

    // 2) Fetch options separately and attach by change_item_id
    const ids = items.map((i: any) => i.id);
    const { data: options, error: optionsError } = await supabase
      .from('change_item_options')
      .select('*')
      .in('change_item_id', ids);

    if (optionsError) {
      throw new Error(`Failed to fetch change item options: ${optionsError.message}`);
    }

    const optionsByChangeId = (options || []).reduce((acc: Record<string, any[]>, opt: any) => {
      (acc[opt.change_item_id] = acc[opt.change_item_id] || []).push(opt);
      return acc;
    }, {} as Record<string, any[]>);

    const merged = items.map((item: any) => ({
      ...item,
      options: optionsByChangeId[item.id] || []
    }));

    return merged as unknown as ChangeItem[];
  }

  /**
   * Update a change item's chosen option
   */
  static async selectOption(changeItemId: string, optionId: string): Promise<void> {
    // First, unselect all options for this change item
    await supabase
      .from('change_item_options')
      .update({ selected_bool: false })
      .eq('change_item_id', changeItemId);

    // Then select the chosen option
    const { error: selectError } = await supabase
      .from('change_item_options')
      .update({ selected_bool: true })
      .eq('id', optionId);

    if (selectError) {
      throw new Error(`Failed to select option: ${selectError.message}`);
    }

    // Update the change item with the chosen option
    const { error: updateError } = await supabase
      .from('change_items')
      .update({ chosen_option_id: optionId })
      .eq('id', changeItemId);

    if (updateError) {
      throw new Error(`Failed to update change item: ${updateError.message}`);
    }
  }

  /**
   * Add user tweaks to a change item
   */
  static async addUserTweak(changeItemId: string, tweakText: string): Promise<void> {
    const { error } = await supabase
      .from('change_items')
      .update({ user_tweak_text: tweakText })
      .eq('id', changeItemId);

    if (error) {
      throw new Error(`Failed to add user tweak: ${error.message}`);
    }
  }

  /**
   * Apply a change item (mark as completed)
   */
  static async applyChange(changeItemId: string): Promise<void> {
    const { error } = await supabase
      .from('change_items')
      .update({ status: 'completed' })
      .eq('id', changeItemId);

    if (error) {
      throw new Error(`Failed to apply change: ${error.message}`);
    }
  }

  /**
   * Generate accuracy report for a session
   */
  static async generateAccuracyReport(sessionId: string): Promise<{
    totalChanges: number;
    exactMatches: number;
    partialMatches: number;
    notPossible: number;
    overallScore: number;
    changeLog: any[];
  }> {
    const changeItems = await this.getChangeItems(sessionId);
    
    const completedItems = changeItems.filter(item => item.status === 'completed');
    const totalChanges = changeItems.length;
    
    // Calculate accuracy metrics
    let exactMatches = 0;
    let partialMatches = 0;
    let notPossible = 0;

    const changeLog = completedItems.map(item => {
      const selectedOption = item.options?.find(opt => opt.selected_bool);
      
      // Determine accuracy status based on option score and user tweaks
      let status = 'exact';
      if (selectedOption) {
        if (selectedOption.score < 90 || item.user_tweak_text) {
          status = 'partial';
          partialMatches++;
        } else {
          exactMatches++;
        }
      } else {
        status = 'not_possible';
        notPossible++;
      }

      return {
        id: item.id,
        instruction: item.instruction_text,
        status,
        chosenOption: selectedOption?.text || 'No option selected',
        alternativeOptions: item.options?.filter(opt => !opt.selected_bool).map(opt => opt.text) || [],
        userTweaks: item.user_tweak_text,
        aiReasoning: selectedOption?.reasons.join(', ') || 'No reasoning available',
        timestamp: item.updated_at
      };
    });

    const overallScore = totalChanges > 0 
      ? Math.round(((exactMatches * 100 + partialMatches * 60) / totalChanges))
      : 0;

    // Update session with accuracy report
    const accuracyReport = {
      totalChanges,
      exactMatches,
      partialMatches,
      notPossible,
      overallScore,
      generatedAt: new Date().toISOString()
    };

    await supabase
      .from('revision_sessions')
      .update({ 
        accuracy_report: accuracyReport,
        status: 'completed'
      })
      .eq('id', sessionId);

    return {
      ...accuracyReport,
      changeLog
    };
  }

  /**
   * Get all revision sessions for the current user
   */
  static async getRevisionSessions(): Promise<RevisionSession[]> {
    const { data: sessions, error } = await supabase
      .from('revision_sessions')
      .select(`
        *,
        change_items:change_items(*)
      `)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch revision sessions: ${error.message}`);
    }

    return (sessions || []) as unknown as RevisionSession[];
  }

  /**
   * Get a specific revision session with all its data
   */
  static async getRevisionSession(sessionId: string): Promise<RevisionSession | null> {
    const { data: session, error } = await supabase
      .from('revision_sessions')
      .select(`
        *,
        change_items:change_items(*)
      `)
      .eq('id', sessionId)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to fetch revision session: ${error.message}`);
    }

    return session as unknown as RevisionSession;
  }

  /**
   * Delete a revision session and all its related data
   */
  static async deleteRevisionSession(sessionId: string): Promise<void> {
    const { error } = await supabase
      .from('revision_sessions')
      .delete()
      .eq('id', sessionId);

    if (error) {
      throw new Error(`Failed to delete revision session: ${error.message}`);
    }
  }
}